"use client";

import React, { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Droplets, ShieldCheck, Search, ChevronRight, CircleCheck as CheckCircle2, PhoneCall, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
declare const wvcClient: {
  getCurrentWPQueryParams?: () => Record<string, string | undefined>;
} | undefined;
export default function ShopHero() {
  const shouldReduceMotion = useReducedMotion();
  const searchTerm = useMemo(() => {
    try {
      const params = typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQueryParams === "function" ? wvcClient.getCurrentWPQueryParams() : {};
      return String(params?.s ?? params?.search ?? "").trim();
    } catch {
      return "";
    }
  }, []);
  const features = [{
    text: "Repuestos y equipos 100% originales"
  }, {
    text: "Garantía oficial en todos los productos"
  }, {
    text: "Asesoramiento técnico profesional"
  }];
  return <section id="shop-hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border">
      {/* Decorative ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        <div className="absolute -top-32 right-0 w-96 h-96 rounded-full bg-accent blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 w-80 h-80 rounded-full bg-muted blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Breadcrumb row */}
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Link to="/HomePage" className="hover:text-foreground transition-colors">
            Inicio
          </Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground font-medium">Tienda & Equipamiento</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Main Title & Description Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border text-foreground text-xs font-semibold uppercase tracking-wide mb-4 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-chart-1" />
              <span>Catálogo Técnico Especializado</span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-4">
              {searchTerm ? <>
                  Resultados para: <span className="text-chart-1">"{searchTerm}"</span>
                </> : <>
                  Equipos, repuestos y material para <span className="underline decoration-chart-1 decoration-4 underline-offset-4">fontanería y climatización</span>
                </>}
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-6">
              {searchTerm ? `Explora los productos y accesorios profesionales que coinciden con tu búsqueda en Totana, Murcia y alrededores.` : `Accede a recambios garantizados para detección de fugas, piscinas, climatización y fontanería técnica. Suministros directos con asesoría de instaladores expertos.`}
            </p>

            {/* Value items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mb-8">
              {features.map((feature, index) => <div key={index} data-index={index} className="flex items-center gap-2 rounded-lg bg-card p-2.5 border border-border shadow-xs text-xs md:text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-chart-1 shrink-0" />
                  <span>{feature.text}</span>
                </div>)}
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-4 w-full">
              <Link to="/ContactPage">
                <Button className="bg-primary text-primary-foreground font-semibold px-6 py-2.5 rounded-md shadow-md hover:bg-primary/90 transition-colors">
                  <Wrench className="w-4 h-4" />
                  Pedir asesoría o presupuesto
                </Button>
              </Link>
              <Link to="/Emergency24HoursPage">
                <Button variant="outline" className="border-border text-foreground bg-card hover:bg-accent font-medium px-5 py-2.5 rounded-md transition-colors">
                  <PhoneCall className="w-4 h-4 text-chart-1" />
                  Servicio Técnico Urgente
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Side Visual Card / Highlights */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl bg-card border border-border p-6 shadow-lg">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center text-foreground">
                    <Droplets className="w-5 h-5 text-chart-1" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-foreground">Material Certificado</h2>
                    <p className="text-xs text-muted-foreground">Instalación y reposición en Totana</p>
                  </div>
                </div>
                <div className="px-2.5 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                  Servicio Pro
                </div>
              </div>

              {/* Service/Category Chips */}
              <div className="space-y-3 mb-5">
                <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-chart-1 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Bombas & Filtros de Piscina</p>
                      <p className="text-[11px] text-muted-foreground">Stock permanente y sustitución rápida</p>
                    </div>
                  </div>
                  <Link to="/PoolsPage" className="text-xs font-semibold text-foreground hover:underline">
                    Ver
                  </Link>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-3">
                    <Search className="w-4 h-4 text-chart-1 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Geófonos & Sensores de Fugas</p>
                      <p className="text-[11px] text-muted-foreground">Tecnología no invasiva acústica</p>
                    </div>
                  </div>
                  <Link to="/LeakDetectionPage" className="text-xs font-semibold text-foreground hover:underline">
                    Ver
                  </Link>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-3">
                    <Wrench className="w-4 h-4 text-chart-1 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">Grifería & Climatización</p>
                      <p className="text-[11px] text-muted-foreground">Válvulas, termostatos y split</p>
                    </div>
                  </div>
                  <Link to="/PlumbingPage" className="text-xs font-semibold text-foreground hover:underline">
                    Ver
                  </Link>
                </div>
              </div>

              {/* Support Notice */}
              <div className="p-3.5 rounded-xl bg-accent text-accent-foreground flex items-center justify-between gap-3 text-xs">
                <p className="font-medium leading-tight">
                  ¿Dudas sobre compatibilidad? Nuestro equipo técnico te ayuda antes de comprar.
                </p>
                <Link to="/ContactPage">
                  <Button size="sm" className="bg-primary text-primary-foreground font-semibold text-xs px-3 py-1.5 shrink-0 rounded-md">
                    Consultar
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}